import { Braces } from 'lucide-react';
import type { AbiParameter } from 'viem';
import { ParameterInput } from './ParameterInput';
import {
  TransactionBuilderParameterInputProps,
  TransactionBuilderParameterState,
} from './types';
import { getParameterStateForStructOrTuple } from './utils';
import { getReadableSolidityName } from '@/lib/solidity-formatting';

export function StructInput({
  value,
  name,
  onChange,
  paramAbiFragment,
  networkId,
  allowUnsavedAddress = false,
  addressBookEntryByAddress,
  addressBookLookupPending = false,
  addressBookLookupFailed = false,
}: TransactionBuilderParameterInputProps): React.ReactElement {
  /**
   * Each child element in the struct/tuple
   */
  const childElements: TransactionBuilderParameterState[] = JSON.parse(
    value || '[]'
  );

  /**
   * Returns the `onChange` handler for a child element at a given index
   * This is required to ensure that the handler has access to the correct child element index
   * so that it updates the correct child
   *
   * @param {number} childElementIndex Index of the struct/tuple child element that's been updated
   * @returns Handler for updating the state of the struct/tuple child element
   */
  function getChildElementOnChangeEventHandler(
    childElementIndex: number
  ): (_: string, newParameterState: TransactionBuilderParameterState) => void {
    /**
     * `onChange` callback for the child element parameters
     *
     * Updates the `childElements` to reflect changes in the struct/tuple child parameters
     *
     * @param {string} _ (NOT USED) Name of the child element parameter that changed
     * @param {TransactionBuilderParameterState} newParameterState New state of the child element parameter that changed
     */
    return function handleChildElementOnChangeEvent(
      _: string,
      newParameterState: TransactionBuilderParameterState
    ): void {
      // Copy child elements (need to copy so that react state will detect update and re-render)
      const newChildElements: TransactionBuilderParameterState[] = [
        ...childElements,
      ];

      // In-place update the state for the element
      newChildElements.splice(childElementIndex, 1, newParameterState);

      // Case: no `onChange` callback defined
      if (!onChange) return;

      // Case: `onChange` callback defined
      // Call callback with new struct/tuple state
      const newFieldState: TransactionBuilderParameterState =
        getParameterStateForStructOrTuple(newChildElements);
      onChange(name, newFieldState);
    };
  }

  const components = (
    paramAbiFragment as AbiParameter & {
      components?: AbiParameter[];
    }
  ).components;

  if (!components) {
    return (
      <div className="text-sm text-red-500">
        Invalid struct/tuple definition for {paramAbiFragment.name || name}
      </div>
    );
  }

  // Case: able to render struct/tuple inputs
  return (
    <div className="w-full">
      <fieldset className="rounded-[16px] border border-zinc-300 dark:border-zinc-700 p-4 bg-zinc-50/50 dark:bg-zinc-900/50">
        <legend className="flex items-center gap-1.5 px-2 text-sm font-medium text-zinc-900 dark:text-white">
          <Braces className="h-4 w-4" />
          {getReadableSolidityName(name, 'Parameter')}
        </legend>

        <div className="flex flex-col gap-4 pl-2">
          {/* Display inputs for each element */}
          {childElements.map(
            (item: TransactionBuilderParameterState, index: number) => {
              const component = components[index];
              if (!component) return null;

              return (
                <div key={index}>
                  <ParameterInput
                    name={
                      component.name ||
                      getParameterNameFromChildElementIndex(index)
                    }
                    value={item.value}
                    unformattedValue={(item.parsedValue as string) || ''}
                    isValid={item.isValid}
                    isEmpty={item.isEmpty}
                    networkId={networkId}
                    paramAbiFragment={component}
                    onChange={getChildElementOnChangeEventHandler(index)}
                    allowUnsavedAddress={allowUnsavedAddress}
                    addressBookLookupPending={addressBookLookupPending}
                    addressBookLookupFailed={addressBookLookupFailed}
                    {...(addressBookEntryByAddress !== undefined
                      ? { addressBookEntryByAddress }
                      : {})}
                  />
                </div>
              );
            }
          )}
        </div>
      </fieldset>
    </div>
  );
}

/**
 * Constructs the parameter name for a struct/tuple child element based on
 * its index: e.g. "item-0"
 *
 * @param {number} childElementIndex Index of the child element
 * @returns {string} The parameter name for the child element at `childElementIndex`
 */
function getParameterNameFromChildElementIndex(
  childElementIndex: number
): string {
  return `item-${childElementIndex}`;
}
